import { readToplineReportingWindow } from "./toplineReportingWindow";
import { buildPerformanceTimeline } from "./performanceTimelineModel";
/*
 * Platform Analytics Experience — pure view model.
 *
 * One function maps a governed data payload (live topline result or labeled
 * design fixture) plus a platform profile into everything the redesigned
 * page renders. No fetching, no formatting side effects, no derivation of
 * spend. Live efficiency values are forwarded exactly as the analytics
 * topline service computed them; only the fixture design preview derives
 * per-bucket efficiency, with one canonical formula per metric.
 */

import {
  resolvePlatformMetric,
  formatPlatformMetricValue,
  isFiniteMetricNumber,
  profileSupportsSiteVisitsOverlay,
} from "./platformAnalyticsProfiles";
import { resolveObservedSiteVisits } from "./observedSiteVisits";
import {
  CLIENT_BILLED_REPORTING_BASIS,
  formatReportingBasisLabel,
} from "./observedReportingWindow";
import {
  REPORTING_SOURCE_STATES,
  describeReportingSourceForInterpretation,
  resolveReportingSourceReadiness,
} from "./reportingSourceReadiness";

// Cards, charts and spoken summaries share the actual response period.
// Missing period evidence cannot borrow dates from the selection control.

function toFiniteOrNull(value) {
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return isFiniteMetricNumber(value) ? value : null;
}

/*
 * Fixture-only per-bucket efficiency derivations (design preview). Live data
 * never passes through these — the service's canonical ratios are used.
 */
const FIXTURE_EFFICIENCY_DERIVATIONS = {
  ctr: (m) =>
    m.clicks != null && m.impressions > 0 ? (m.clicks / m.impressions) * 100 : null,
  vcr: (m) =>
    m.videoCompletions != null && m.videoStarts > 0
      ? (m.videoCompletions / m.videoStarts) * 100
      : null,
  vvr: (m) =>
    (m.trueViewViews ?? m.videoViews) != null && m.impressions > 0
      ? ((m.trueViewViews ?? m.videoViews) / m.impressions) * 100
      : null,
};

/*
 * The name shown when a payload declared no source of its own. These are
 * descriptions of the delivery basis, never an identity — a source that names
 * itself always wins over them.
 */
const LIVE_SOURCE_STATE_LABELS = {
  [REPORTING_SOURCE_STATES.LIVE]: "Live Feed",
  [REPORTING_SOURCE_STATES.INGESTED]: "Ingested",
  [REPORTING_SOURCE_STATES.PARTIAL]: null,
  [REPORTING_SOURCE_STATES.UNRESOLVED]: "No data",
};

/*
 * A metric the governed topline does not carry as a single field, resolved
 * through the canonical engine over the counts it DOES carry.
 *
 * This is the only derivation live data is allowed, and it is not arithmetic
 * living in a page: `resolveCostPerCompletedView` folds this page's own
 * platform row through `aggregateMetricBasis`, the same registry entry the
 * cross-platform strip and the backend's derivedMetrics read. The spend it
 * divides is `values.spend` — literally the number in the Spend card beside
 * it — so the basis of the two can never diverge.
 *
 * Returns `{ value, unavailableReason }`, or null when this metric is not
 * derived at all and should be read straight off the topline.
 */
function buildCommandMetrics(profile, values) {
  return [...profile.commandMetricKeys, ...(profile.analyticsOnlyMetricKeys || [])]
    .map((key) => resolvePlatformMetric(key))
    .filter(Boolean)
    .map((metric) => {
      const value = toFiniteOrNull(values[metric.key]);
      const envelope =
        metric.key === "cpqv"
          ? values.costPerQualifiedVideoView
          : metric.key === "cpcv"
          ? values.costPerCompletedView
          : null;
      return {
        key: metric.key,
        label: metric.label,
        unit: metric.unit,
        kind: metric.kind,
        value,
        definition: envelope?.formula || metric.definition || null,
        unavailableReason:
          value != null
            ? null
            : envelope?.unavailableMessage || envelope?.unavailableReason?.message
            ? envelope.unavailableMessage || envelope.unavailableReason.message
            : "Not reported for this window",
        spendBasis: envelope?.spendBasis || null,
        spendBasisLabel: envelope?.spendBasisLabel || null,
        selectable: metric.kind === "volume" && profile.chartMetricKeys.includes(metric.key),
      };
    });
}

function chartTextSummary({ profile, metric, buckets, window, sourceKind, disclosure, siteVisits }) {
  if (!buckets.length) return `No delivered ${metric?.label || "metric"} in the current scope.`;
  const total = buckets.reduce((sum, bucket) => sum + (bucket.value || 0), 0);
  const peak = buckets.reduce((best, bucket) => (bucket.value > (best?.value || -Infinity) ? bucket : best), null);
  const windowText = window?.label ? ` across ${window.label}` : "";
  const sourceText =
    sourceKind === "design_fixture"
      ? " Values are governed design-fixture data, not canonical reporting."
      : disclosure
      ? ` ${disclosure}`
      : "";
  return (
    `${profile.heroLabel} delivered a total of ${Math.round(total).toLocaleString("en-US")} ` +
    `${metric?.label || ""}${windowText} over ${buckets.length} data point${buckets.length === 1 ? "" : "s"}` +
    `${peak && buckets.length > 1 ? `, peaking at ${Math.round(peak.value).toLocaleString("en-US")} on ${peak.label}` : ""}.` +
    sourceText +
    siteVisitsTextSummary(siteVisits)
  );
}

/*
 * The spoken description of the overlay.
 *
 * It says whose traffic this is, because the chart cannot: a line drawn over
 * YouTube's bars will be read as YouTube's traffic unless something states
 * otherwise, and neither GA4 sessions nor campaign-attributed visits carry
 * per-platform attribution. It also names the days the series observed that
 * this platform did not deliver on — those observations are real and are
 * deliberately given no column, so the only honest place for them is here
 * rather than silently discarded.
 *
 * The series names ITSELF. An advertiser-wide GA4 overlay and a campaign's
 * verified attributed visits are different measurements with different claims
 * attached, and reciting one vocabulary over both is how a governed outcome
 * came to be read out under its provenance instead of its name.
 */
function siteVisitsTextSummary(siteVisits) {
  if (!siteVisits?.available) return "";
  const plotted = siteVisits.plottedDays || 0;
  const offAxis = siteVisits.offAxisDays || 0;
  const label = siteVisits.label || "Site Visits · GA4";
  const attributed = siteVisits.metric === "ad_attributed_site_visits";
  return (
    ` ${label} overlays ${
      attributed
        ? "the campaign's verified site-wide tracker fires, click-through and view-through,"
        : "observed sessions for the advertiser's site"
    } on ` +
    `${plotted} of these date${plotted === 1 ? "" : "s"}, read on the ${siteVisits.axisLabel || "right-hand axis"}. ` +
    `It measures the ${
      attributed ? "whole campaign" : "site"
    } during the same window, not ${
      attributed ? "visits" : "traffic"
    } attributed to ${
      siteVisits.platformLabel || "this platform"
    }; a date with no observation is left blank rather than drawn as zero.` +
    (offAxis
      ? ` ${
          attributed ? "The campaign" : "GA4"
        } also observed ${offAxis} date${offAxis === 1 ? "" : "s"} this platform reported no ` +
        `delivery on; those observations are counted here but given no column, because this chart's ` +
        `columns are delivery.`
      : "")
  );
}

const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/*
 * A reported day is labeled from the date string itself, never through a Date.
 * Parsing "2026-08-10" yields UTC midnight, which renders as the 9th for any
 * viewer west of Greenwich — a reporting surface cannot move an observation to
 * a day the source did not report it on.
 */
function formatReportedDay(isoDay) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDay || ""));
  if (!match) return String(isoDay || "");
  const month = MONTH_ABBREVIATIONS[Number(match[2]) - 1];
  return month ? `${month} ${Number(match[3])}` : String(isoDay);
}

const WINDOW_TOTAL_DISCLOSURE =
  "Daily delivery breakdown activates when the reporting source reports by day. " +
  "Until then this chart shows the reported window total — never an interpolated trend.";

/*
 * The delivery chart's buckets, and the honest sentence that goes with them.
 *
 * Dated columns are drawn only from days the source actually reported. A day
 * with no observation for the selected metric contributes no column, because
 * drawing it at zero would state a fact the source never reported; a day the
 * source reported AS zero keeps its zero, because that one is an observation.
 *
 * The dated series is used only when the days it draws add up to the window
 * total shown above the chart. Nothing is scaled or distributed to make that
 * true — when it is not true, the shortfall is either declared beside the
 * columns or the chart falls back to the window total, so the bars can never
 * quietly disagree with the KPI they sit under.
 */
function buildLiveChartBuckets({
  profile,
  dailyDelivery,
  metricKey,
  metric,
  windowValue,
  windowLabel,
  siteVisits = null,
  siteVisitsEntitled = false,
}) {
  const days = Array.isArray(dailyDelivery?.days) ? dailyDelivery.days : [];
  const reportedDays = days.filter((day) => toFiniteOrNull(day?.metrics?.[metricKey]) != null);
  /*
   * The join is BY OBSERVATION DATE, never by position. The two series come
   * from different sources with different reporting cadences and different
   * gaps; lining them up by index would silently shift an observation onto a
   * day it was not made on the first time either side skipped a day.
   */
  /*
   * Normalized and NAMED by the shared resolver rather than read off the
   * payload here — the same module the welcome card, the cross-platform page
   * and the public report locate this series through. Its `label`/`metric` are
   * what let the overlay state the governed outcome it actually is instead of
   * the GA4 provenance every surface used to assume.
   */
  const observedSiteVisits = siteVisitsEntitled
    ? resolveObservedSiteVisits(siteVisits)
    : null;
  const visitsByDate = new Map(
    observedSiteVisits ? observedSiteVisits.days.map((day) => [day.date, day.value]) : []
  );

  if (reportedDays.length) {
    const buckets = reportedDays.map((day) => ({
      key: day.date,
      date: day.date,
      label: formatReportedDay(day.date),
      value: toFiniteOrNull(day.metrics[metricKey]),
      rows: buildDayRows(profile, day.metrics, metricKey),
      /*
       * `has`, not a truthy read: a genuinely reported 0 sessions is an
       * observation and must survive as a plotted zero, while an unreported
       * day must stay null so the line breaks instead of dropping to the
       * baseline.
       */
      siteVisits: visitsByDate.has(day.date) ? visitsByDate.get(day.date) : null,
    }));
    const plottedVisitDays = buckets.filter((bucket) => bucket.siteVisits != null).length;
    const drawnDates = new Set(buckets.map((bucket) => bucket.date));
    const offAxisVisitDays = Array.from(visitsByDate.keys()).filter(
      (date) => !drawnDates.has(date)
    ).length;
    const visits = {
      available: plottedVisitDays > 0,
      plottedDays: plottedVisitDays,
      offAxisDays: offAxisVisitDays,
      observedDays: visitsByDate.size,
      platformLabel: profile.heroLabel,
      /* The legend entry and the spoken description both read these; the
         series declares them, no surface picks them. */
      label: observedSiteVisits?.label || null,
      metric: observedSiteVisits?.metric || null,
    };
    /*
     * A metric the SOURCE declares cannot be added up across days — a distinct
     * count, where one person returning on three days is three daily
     * observations and one window figure.
     *
     * The reconciliation gate below is the right test for an additive metric
     * and the wrong one here: these days are never meant to sum to the window,
     * so measuring them against it would reject a perfectly good series and
     * fall back to a single bar. The days are drawn, the window total above
     * stays the source's own authoritative count, and the sentence says which
     * is which instead of letting a reader add the columns up and find a third
     * number.
     */
    const nonAdditiveKeys = Array.isArray(dailyDelivery.nonAdditiveMetricKeys)
      ? dailyDelivery.nonAdditiveMetricKeys
      : [];
    if (nonAdditiveKeys.includes(metricKey)) {
      const label = (metric?.label || metricKey).toLowerCase();
      const windowText =
        windowValue == null
          ? "the window total above"
          : `the ${formatDisclosureValue(metric, windowValue)} above`;
      return {
        mode: "daily",
        buckets,
        dailyPending: false,
        siteVisits: visits,
        disclosure:
          `Each column is the ${label} the source counted on that day. A distinct count ` +
          `does not add up across days — someone counted on two days is counted once for ` +
          `the window — so these columns do not sum to ${windowText}, which is the ` +
          `source's own count for the whole window.`,
      };
    }

    const dailyTotal = buckets.reduce((sum, bucket) => sum + bucket.value, 0);
    const remainder = windowValue == null ? null : windowValue - dailyTotal;
    const reconciles = remainder != null && Math.abs(remainder) < 0.005;

    if (reconciles && dailyDelivery.complete !== false) {
      return { mode: "daily", buckets, dailyPending: false, disclosure: null, siteVisits: visits };
    }

    /*
     * Evidence that names days is still worth drawing when part of the window
     * cannot be placed on one — a cumulative span counts toward the total but
     * belongs to no single date. What must not happen is that shortfall being
     * spread across the days or passed over in silence.
     */
    if (remainder != null && remainder > 0) {
      return {
        mode: "daily",
        buckets,
        dailyPending: false,
        siteVisits: visits,
        disclosure:
          `Dated columns cover ${formatDisclosureValue(metric, dailyTotal)} of the window's ` +
          `${formatDisclosureValue(metric, windowValue)} ${metric?.label || metricKey}. The remaining ` +
          `${formatDisclosureValue(metric, remainder)} comes from evidence that reports a span rather ` +
          `than a day, so it is counted in the total but drawn on no date.`,
      };
    }

    /*
     * The days total MORE than the window.
     *
     * For a source that attributes a value to every date it touches, that is
     * expected rather than alarming: a session spanning midnight is counted on
     * both days and once for the window. The days are real observations and are
     * drawn; the overshoot is stated, and the window figure above stays the
     * source's own count.
     *
     * This case had no branch at all, so it fell through to the window total
     * and captioned itself "the reporting source does not report by day" — of a
     * source that had just reported three of them. An overshoot of 41 sessions
     * in 11,721 was enough to hide a whole daily series behind a claim that was
     * simply untrue.
     */
    const overlappingKeys = Array.isArray(dailyDelivery.dateOverlappingMetricKeys)
      ? dailyDelivery.dateOverlappingMetricKeys
      : [];
    if (remainder != null && remainder < 0 && overlappingKeys.includes(metricKey)) {
      const label = (metric?.label || metricKey).toLowerCase();
      return {
        mode: "daily",
        buckets,
        dailyPending: false,
        siteVisits: visits,
        disclosure:
          `Dated columns total ${formatDisclosureValue(metric, dailyTotal)} against the window's ` +
          `${formatDisclosureValue(metric, windowValue)} ${label}. The source counts a ${label.replace(/s$/, "")} ` +
          `on every date it touches — one spanning midnight falls on both days but once in the ` +
          `window — so the columns run ${formatDisclosureValue(metric, Math.abs(remainder))} over. ` +
          `The figure above is the source's own count for the whole window.`,
      };
    }

    /*
     * Days exist, but they cannot be squared with the window and the source has
     * not declared why. That is worth refusing to draw — but it is NOT the same
     * fact as "this source cannot report by day", and the caller must be able to
     * tell a reader which of the two happened.
     */
    return {
      mode: "window_total",
      buckets:
        windowValue != null
          ? [
              {
                key: "window_total",
                date: null,
                label: windowLabel || "Analysis window",
                value: windowValue,
                rows: [],
              },
            ]
          : [],
      dailyPending: false,
      disclosure:
        windowValue == null
          ? null
          : `The source reported ${buckets.length} dated ${
              buckets.length === 1 ? "day" : "days"
            } for this window, but they total ${formatDisclosureValue(metric, dailyTotal)} against ` +
            `its own window figure of ${formatDisclosureValue(metric, windowValue)}. Until that gap ` +
            `is explained the window total is shown rather than a daily trend built on days that ` +
            `disagree with it.`,
      siteVisits: { available: false, plottedDays: 0, offAxisDays: 0, observedDays: 0 },
    };
  }

  const windowBucket =
    windowValue != null
      ? [
          {
            key: "window_total",
            date: null,
            label: windowLabel || "Analysis window",
            value: windowValue,
            rows: [],
          },
        ]
      : [];

  /*
   * No time axis, so no time series. A window total is one certified number for
   * the whole period; drawing a dated GA4 line across it would manufacture a
   * trend for the media beneath it that the source never reported. The
   * observation is not lost — it is simply not something this state can draw.
   */
  return {
    mode: "window_total",
    buckets: windowBucket,
    dailyPending: true,
    disclosure: windowBucket.length ? WINDOW_TOTAL_DISCLOSURE : null,
    siteVisits: { available: false, plottedDays: 0, offAxisDays: 0, observedDays: 0 },
  };
}

function formatDisclosureValue(metric, value) {
  if (!isFiniteMetricNumber(value)) return "—";
  if (metric?.unit === "currency") {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return Math.round(value).toLocaleString("en-US");
}

/*
 * Everything else this platform reported on the hovered day, so reading one
 * column does not require trusting that the rest of the page still applies to
 * it. Only metrics the day actually carries appear — an absent metric is left
 * out rather than shown as zero.
 */
function buildDayRows(profile, dayMetrics, plottedMetricKey) {
  return profile.commandMetricKeys
    .filter((key) => key !== plottedMetricKey)
    .map((key) => resolvePlatformMetric(key))
    .filter(Boolean)
    .map((entry) => {
      const isYoutubeDailyCtr =
        profile?.platformKey === "youtube" && entry.key === "ctr";
      return {
        key: entry.key,
        label: entry.label,
        /*
         * Only the YouTube daily adapter emits CTR as a ratio. Window KPI and
         * every existing platform percent remain percentage-unit values.
         */
        unit: isYoutubeDailyCtr ? "percent_ratio" : entry.unit,
        value: toFiniteOrNull(
          dayMetrics?.[entry.key] ??
            dayMetrics?.[entry.raw] ??
            (entry.key === "trueViewViews" ? dayMetrics?.videoViews : null)
        ),
      };
    })
    .filter((row) => row.value != null);
}

/*
 * The source SHERLOC is reading, stated on the card itself. It is the same
 * identity Pantheon prints, from the same object — the two cards naming
 * different things is the failure this exists to make impossible.
 */
function buildSherlocSource(readiness) {
  return {
    resolved: readiness.sourceResolved,
    label: readiness.sourceLabel,
    state: readiness.state,
  };
}

function buildSherloc({ profile, values, readiness, pacing, state }) {
  const insights = [];
  if (state !== "ready") {
    if (state === "no_data") {
      insights.push({
        kind: "observed",
        text: `No ${profile.heroLabel} delivery is reported for the current advertiser and analysis window. Nothing is estimated in its place.`,
        evidence: [],
      });
    }
    return insights;
  }

  const spend = toFiniteOrNull(values.spend);
  const impressions = toFiniteOrNull(values.impressions);
  if (spend != null || impressions != null) {
    insights.push({
      kind: "observed",
      text:
        spend != null
          ? `Reported client-billed spend is $${Math.round(spend).toLocaleString("en-US")} with ${
              impressions != null ? impressions.toLocaleString("en-US") : "unreported"
            } impressions in the analysis window.`
          : `${impressions.toLocaleString("en-US")} impressions are reported for the analysis window; spend is not reported.`,
      evidence: ["spend", "impressions"].filter((key) => toFiniteOrNull(values[key]) != null),
    });
  }

  const efficiencyKey = profile.pairedEfficiencyKey;
  const efficiencyMetric = resolvePlatformMetric(efficiencyKey);
  const efficiencyValue = toFiniteOrNull(values[efficiencyKey]);
  if (efficiencyMetric && efficiencyValue != null) {
    insights.push({
      kind: "calculated",
      text: `${efficiencyMetric.label} for the window is ${efficiencyValue.toFixed(2)}${
        efficiencyMetric.unit === "percent" ? "%" : ""
      }. ${efficiencyMetric.definition || ""}`.trim(),
      evidence: [efficiencyKey],
    });
  }

  /*
   * The interpretation reads the SAME resolution object Pantheon prints. It
   * cannot say "no usable source" beside a card naming one, because neither
   * card resolves the source itself any more.
   */
  insights.push({
    kind: "interpretation",
    text: describeReportingSourceForInterpretation(readiness),
    evidence: [],
  });

  if (pacing?.status && pacing.status !== "flight ended") {
    insights.push({
      kind: "recommendation",
      text: `Delivery pacing reads "${pacing.status}"${pacing.meta ? ` (${pacing.meta})` : ""}. ${
        pacing.tone === "alert"
          ? "Review the flight plan against delivered spend before adjusting anything."
          : "No corrective action is indicated by the current pacing read."
      }`,
      evidence: ["spend"],
    });
  }
  return insights;
}

export function buildPlatformAnalyticsViewModel({
  mode = "live",
  profile,
  topline = null,
  toplineError = null,
  fixture = null,
  selectedMetricKey = null,
  nowMs = null,
} = {}) {
  if (!profile) {
    return { state: "error", errorMessage: "Unknown platform profile.", commandMetrics: [], sherloc: [] };
  }

  const chartMetricKey = profile.chartMetricKeys.includes(selectedMetricKey)
    ? selectedMetricKey
    : profile.chartMetricKeys[0];
  const chartMetric = resolvePlatformMetric(chartMetricKey);

  /* ---------------------------------------------------------------- */
  /* Fixture (design preview) mode                                     */
  /* ---------------------------------------------------------------- */
  if (mode === "fixture" && fixture) {
    const values = fixture.rawTotals || {};
    const hasDelivery = (fixture.series || []).length > 0;
    const state = fixture.error ? "error" : hasDelivery ? "ready" : "no_data";
    const buckets = (fixture.series || []).map((bucket) => ({
      key: bucket.date,
      label: bucket.date.slice(5),
      value: toFiniteOrNull(bucket.metrics[chartMetricKey]) || 0,
    }));
    const derive = FIXTURE_EFFICIENCY_DERIVATIONS[profile.pairedEfficiencyKey];
    const efficiencyMetric = resolvePlatformMetric(profile.pairedEfficiencyKey);
    const efficiencyPoints =
      derive && hasDelivery ? fixture.series.map((bucket) => derive(bucket.metrics)) : null;
    /*
     * The design preview resolves through the same contract as live data —
     * one readiness algorithm on this page, whatever filled it. The fixture
     * states its age in minutes, so it is expressed as the stamp the contract
     * reads rather than patched onto the result afterwards.
     */
    const fixtureClock = isFiniteMetricNumber(nowMs) ? nowMs : Date.now();
    const fixtureReadiness = resolveReportingSourceReadiness({
      raw: {
        snapshotSourceLabel: fixture.source.sourceLabel,
        snapshotSourceTier: fixture.source.tier,
        snapshotSourceConfidence: fixture.source.confidence,
        snapshotSourceTimestamp: isFiniteMetricNumber(fixture.source.freshnessMinutes)
          ? new Date(fixtureClock - fixture.source.freshnessMinutes * 60000).toISOString()
          : null,
        snapshotIsLive: fixture.source.isLive === true,
        snapshotIsFallback: fixture.source.isFallback === true,
      },
      hasObservations: state === "ready",
      nowMs: fixtureClock,
    });

    return {
      state,
      errorMessage: fixture.error || null,
      sourceKind: "design_fixture",
      sourceBadge: fixture.source.label,
      window: { label: fixture.window.label, subtext: null },
      commandMetrics: state === "ready" ? buildCommandMetrics(profile, values) : [],
      chart: {
        available: state === "ready",
        mode: "daily",
        dailyPending: false,
        disclosure: null,
        observedDays: buckets.length,
        metricKey: chartMetricKey,
        metricLabel: chartMetric?.label || chartMetricKey,
        metricUnit: chartMetric?.unit || "count",
        buckets,
        /* The design preview carries no GA4 evidence and invents none. */
        siteVisits: { available: false, plottedDays: 0, offAxisDays: 0, observedDays: 0 },
        efficiency:
          efficiencyPoints && efficiencyPoints.some((point) => point != null)
            ? {
                key: profile.pairedEfficiencyKey,
                label: efficiencyMetric?.label || profile.pairedEfficiencyKey,
                unit: efficiencyMetric?.unit || "percent",
                points: efficiencyPoints,
              }
            : null,
        textSummary: chartTextSummary({
          profile,
          metric: chartMetric,
          buckets,
          window: fixture.window,
          sourceKind: "design_fixture",
        }),
      },
      sherloc: buildSherloc({
        profile,
        values,
        readiness: fixtureReadiness,
        pacing: fixture.pacing,
        state,
      }),
      sherlocSource: buildSherlocSource(fixtureReadiness),
      pantheon: {
        assessmentAvailable: true,
        tier: fixture.source.tier,
        sourceLabel: fixture.source.sourceLabel,
        confidence: fixture.source.confidence,
        freshnessMinutes: fixture.source.freshnessMinutes,
        isLive: fixture.source.isLive,
        isFallback: fixture.source.isFallback,
        sourceResolved: fixtureReadiness.sourceResolved,
        state: fixtureReadiness.state,
        statusLabel: fixtureReadiness.statusLabel,
        statusTone: fixtureReadiness.statusTone,
        missingMetadata: fixtureReadiness.missingMetadata,
        notes: fixture.source.notes,
        limitations: fixture.source.limitations,
      },
      pacing: fixture.pacing,
      governance: {
        /* One basis, one sentence, both portals — the shared formatter.
           Net is gated on this surface (see the note below), so the
           values here are client-billed and say only that. */
        basisLabel: formatReportingBasisLabel(CLIENT_BILLED_REPORTING_BASIS),
        netGatedNote: null,
      },
    };
  }

  /* ---------------------------------------------------------------- */
  /* Live mode — governed topline result                               */
  /* ---------------------------------------------------------------- */
  const raw = topline?.raw || null;
  const values = raw || {};
  const hasUsableData = Boolean(raw?.hasAnyData ?? raw?.usableDataFound);
  const state = toplineError && !raw ? "error" : hasUsableData ? "ready" : "no_data";
  /*
   * One resolution, read by both intelligence cards below. Source identity is
   * decided here and nowhere else; readiness is decided here and nowhere else.
   */
  const readiness = resolveReportingSourceReadiness({
    raw,
    hasObservations: hasUsableData,
    nowMs,
  });
  const sourceLabel = readiness.sourceLabel || LIVE_SOURCE_STATE_LABELS[readiness.state] || null;
  const freshnessMinutes = readiness.freshnessMinutes;

  const chartValue = toFiniteOrNull(values[chartMetricKey]);
  /*
   * Whether this PAGE may draw the overlay at all — a property of the surface,
   * decided once, before any evidence is consulted. GA4's own Analytics page
   * and the commerce-only pages are excluded here rather than by hoping the
   * evidence happens to be absent for them.
   */
  const siteVisitsEntitled = profileSupportsSiteVisitsOverlay(profile);
  const reportingWindow = readToplineReportingWindow(topline);
  const windowLabel = reportingWindow.label || "Reporting window unavailable";
  const chartState =
    state === "ready"
      ? buildLiveChartBuckets({
          profile,
          dailyDelivery: raw?.dailyDelivery || null,
          metricKey: chartMetricKey,
          metric: chartMetric,
          windowValue: chartValue,
          windowLabel,
          /*
           * The SAME series regardless of which delivery metric is selected —
           * it is an observation about the advertiser's site, and the media
           * metric selector has no bearing on it.
           */
          siteVisits: raw?.siteVisits || null,
          siteVisitsEntitled,
        })
      : {
          mode: "window_total",
          buckets: [],
          dailyPending: true,
          disclosure: null,
          siteVisits: { available: false, plottedDays: 0, offAxisDays: 0, observedDays: 0 },
        };
  const buckets = chartState.buckets;
  const performance = state === "ready" ? buildPerformanceTimeline({
    profile, dailyDelivery: raw?.dailyDelivery, totals: values,
    selectedMetric: chartMetric,
    extraRowsByDate: Object.fromEntries((raw?.dailyDelivery?.days || []).map(day => [day.date,
      buildDayRows(profile, day.metrics, chartMetricKey)
        // Keep every pre-existing daily reading (including CPQV/CPCV and
        // legacy completion counts); only avoid rows already named by a line.
        .filter(row => !["spend", "impressions", "clicks", "videoViews", "engagements", chartMetricKey].includes(row.key))
        .map(row => ({ ...row, formattedValue: formatPlatformMetricValue(row.unit, row.value) })),
    ])),
    siteVisits: siteVisitsEntitled ? resolveObservedSiteVisits(raw?.siteVisits) : null,
  }) : null;

  return {
    state,
    errorMessage:
      state === "error"
        ? "Live metrics are not available right now. Nothing is estimated in their place."
        : null,
    sourceKind: "live",
    sourceBadge: null,
    requestedWindow: { ...reportingWindow.requested, label: reportingWindow.requestedLabel },
    window: {
      ...reportingWindow.effective,
      recovered: reportingWindow.recovered,
      label: windowLabel,
      subtext: topline?.analysisWindow?.subtext || raw?.analysisWindowSubtext || null,
    },
    commandMetrics: state === "ready" ? buildCommandMetrics(profile, values) : [],
    chart: {
      available: buckets.length > 0 || Boolean(performance),
      performance,
      mode: chartState.mode,
      dailyPending: chartState.dailyPending,
      disclosure: chartState.disclosure,
      observedDays: chartState.mode === "daily" ? buckets.length : 0,
      metricKey: chartMetricKey,
      metricLabel: chartMetric?.label || chartMetricKey,
      metricUnit: chartMetric?.unit || "count",
      buckets,
      efficiency: null,
      /*
       * Measurement, stated as its own channel on the model. It is never a
       * bucket value, never a segment and never part of `commandMetrics` — the
       * page's media totals cannot reach it, which is the structural version of
       * the rule rather than a promise to remember it.
       */
      siteVisits: chartState.siteVisits || {
        available: false,
        plottedDays: 0,
        offAxisDays: 0,
        observedDays: 0,
      },
      textSummary: (performance ? performance.description + " " : "") + chartTextSummary({
        profile,
        metric: chartMetric,
        buckets,
        window: { label: windowLabel },
        sourceKind: "live",
        disclosure: chartState.disclosure,
        siteVisits: performance ? { ...chartState.siteVisits, axisLabel: "count axis" } : chartState.siteVisits,
      }),
    },
    sherloc: buildSherloc({
      profile,
      values,
      readiness,
      pacing: raw
        ? { status: raw.pacingStatus || topline?.pacingStatus || null, meta: topline?.pacingMeta || null, tone: topline?.pacingTone || null }
        : null,
      state,
    }),
    sherlocSource: buildSherlocSource(readiness),
    pantheon: raw
      ? {
          assessmentAvailable: true,
          /* Metadata, exactly as the source declared it — never derived to
             fill the card, and never allowed to decide identity. */
          tier: readiness.tier,
          sourceLabel,
          confidence: readiness.confidence,
          freshnessMinutes,
          isLive: Boolean(raw.snapshotIsLive),
          isFallback: Boolean(raw.snapshotIsFallback),
          /* The canonical readiness, the same object SHERLOC interpreted. */
          sourceResolved: readiness.sourceResolved,
          state: readiness.state,
          statusLabel: readiness.statusLabel,
          statusTone: readiness.statusTone,
          missingMetadata: readiness.missingMetadata,
          notes: Array.isArray(raw.snapshotSourceNotes) ? raw.snapshotSourceNotes : [],
          /*
           * A declaration gap is a limitation of this view, listed beside the
           * source's own — it is stated, never allowed to erase the source.
           */
          limitations: [
            ...(Array.isArray(raw.snapshotSourceLimitations) ? raw.snapshotSourceLimitations : []),
            ...(readiness.metadataLimitation ? [readiness.metadataLimitation] : []),
          ],
        }
      : { assessmentAvailable: false },
    pacing: {
      status: raw?.pacingStatus || topline?.pacingStatus || null,
      meta: topline?.pacingMeta || null,
      tone: topline?.pacingTone || null,
    },
    governance: {
      /* Same sentence, same formatter, live mode. */
      basisLabel: formatReportingBasisLabel(CLIENT_BILLED_REPORTING_BASIS),
      netGatedNote: null,
    },
  };
}
